import type { TRPCClientErrorBase } from "@trpc/client";
import type { ErrorType } from "replo-publisher/src/trpc/trpc";
import type {
  PreviewElementProps,
  ReploElement,
} from "schemas/generated/element";

import React, { useState } from "react";

import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import SplitButton from "@v1/components/designSystem/button/SplitButton";
import usePreviewBeforePublishInfo from "@v1/hooks/usePreviewBeforePublishInfo";
import { useTemplateProductPublishingInfo } from "@v1/hooks/usePublishedInfo";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import {
  selectDraftElementCurrentVersion,
  selectDraftElementId,
  selectDraftElementType,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { ConnectShopifyCallout } from "@v2/components/ConnectShopifyCallout";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useOpenModal } from "@v2/store/modalStore";
import { elementRouter } from "@v2/trpc/routes/element";
import { docs } from "@v2/utils/docs";
import copy from "copy-to-clipboard";
import { ArrowUpRight, Copy } from "lucide-react";
import { QRCode } from "react-qrcode-logo";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { PreviewLinkAndSVGSkeleton } from "../editor/SkeletonLoaders";

type PreviewPageButtonProps = {
  onCreatePreviewData: () => PreviewElementProps | undefined;
  isStoreClosed: boolean;
  isProjectConnectedToShopify: boolean;
};

function shouldShowPreviewsForElementType(
  draftElementType: ReploElement["type"],
): boolean {
  return exhaustiveSwitch({ type: draftElementType })({
    page: true,
    shopifyArticle: false,
    shopifySection: true,
    shopifyProductTemplate: true,
  });
}

const PreviewPageButton: React.FC<PreviewPageButtonProps> = ({
  onCreatePreviewData,
  isStoreClosed,
  isProjectConnectedToShopify,
}) => {
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const logEvent = useLogAnalytics();
  const errorToast = useV1ErrorToast();
  const [isPreviewPopoverOpen, setIsPreviewPopoverOpen] = React.useState(false);
  const { url: previewUrl } = usePreviewBeforePublishInfo();
  const { isProductTemplateWithFakeProduct } =
    useTemplateProductPublishingInfo();
  const [
    lastCreatedPreviewElementVersion,
    setLastCreatedPreviewElementVersion,
  ] = React.useState<number | null>(null);
  const currentElementVersion = useEditorSelector(
    selectDraftElementCurrentVersion,
  );
  const draftElementType = useEditorSelector(selectDraftElementType);
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();

  const [lastCreatedPreviewElementId, setLastCreatedPreviewElementId] =
    React.useState<string | null>(null);
  const currentElementId = useEditorSelector(selectDraftElementId);
  const openModal = useOpenModal();
  const {
    mutateAsync: createPreviewElement,
    isPending: isPreviewCreateLoading,
    isSuccess: isPreviewCreateSuccessful,
  } = elementRouter.createPreview.useMutation({
    onError: (error) => {
      handleCreatePreviewElementError(error);
    },
  });

  function handleCreatePreviewElementError(
    error: TRPCClientErrorBase<ErrorType>,
  ) {
    setIsPreviewPopoverOpen(false);
    if ("status" in error && error.status === 404) {
      errorToast(
        "Page Not Found",
        "The page you are trying to preview does not exist. Maybe it was deleted by another user?",
        "error.page.preview.not_found",
        {
          error: error.message,
        },
      );
    } else {
      errorToast(
        "Error",
        "Something went wrong. Please try again later.",
        "error.page.preview.not_found.no_404",
        {
          error: error.message,
        },
      );
    }
  }

  function showBundleBuilderPreviewWarningIfNecessary({
    previewData,
    shouldRedirect,
  }: {
    previewData: PreviewElementProps;
    shouldRedirect?: boolean;
  }) {
    const doesElementIncludeBundleDiscounts = Object.values(
      previewData.dependencies,
    ).some((dependencyArray) => {
      return dependencyArray.some(({ type }) => type === "bundleConfig");
    });
    if (doesElementIncludeBundleDiscounts) {
      openModal({
        type: "fullPageErrorModal",
        props: {
          details: {
            header: "Bundle Discount is not active",
            message: (
              <span>
                When previewing a page with a Bundle Builder, the associated
                bundle discounts are not active on your store. You will be able
                to preview the functionality of the Bundle Builder and add items
                to your cart, but they will not have discounts reflected in your
                cart.{" "}
                <a
                  className="text-primary hover:underline"
                  href={docs.bundleBuilder}
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn More.
                </a>
              </span>
            ),
            isRecoverable: true,
            callToAction: shouldRedirect
              ? {
                  type: "link",
                  name: "Continue",
                  to: previewUrl,
                  target: "_blank",
                }
              : {
                  type: "closeModal",
                  name: "Continue",
                },
          },
        },
      });
    } else if (shouldRedirect) {
      window.open(previewUrl, "_blank");
    }
  }

  async function onPopoverOpen(isPreviewPopoverOpen: boolean) {
    setIsPreviewPopoverOpen(isPreviewPopoverOpen);
    logEvent("preview.requested", {});
    if (
      isPreviewPopoverOpen &&
      (currentElementVersion !== lastCreatedPreviewElementVersion ||
        currentElementId !== lastCreatedPreviewElementId)
    ) {
      const previewData = onCreatePreviewData();
      if (previewData) {
        await createPreviewElement(previewData);
        setLastCreatedPreviewElementVersion(currentElementVersion ?? null);
        setLastCreatedPreviewElementId(currentElementId ?? null);
        showBundleBuilderPreviewWarningIfNecessary({ previewData });
      }
    }
  }

  const popoverProps = isProjectConnectedToShopify
    ? {
        isOpen: isPreviewPopoverOpen,
        onOpenChange: (isOpen: boolean) => void onPopoverOpen(isOpen),
        content: (
          <PublishPreview
            isPreviewCreateLoading={isPreviewCreateLoading}
            isPreviewCreateSuccessful={isPreviewCreateSuccessful}
          />
        ),
        title: "Preview Options",
        UNSAFE_titleClassnames: "text-sm font-semibold leading-5",
        className: "w-[230px]",
        sideOffset: 5,
        skipTriggerStyles: true,
        tooltipText: "Preview Options",
        ariaLabel: "Preview Options",
      }
    : {
        content: (
          <ConnectShopifyCallout
            cookieValue={{
              type: "preview",
              projectId: projectId!,
              workspaceId: workspaceId!,
            }}
          />
        ),
        className: "w-72",
        sideOffset: 16,
        hideCloseButton: true,
      };

  const isPreviewDisabled =
    isStoreClosed ||
    isProductTemplateWithFakeProduct ||
    !shouldShowPreviewsForElementType(draftElementType);

  return (
    <SplitButton
      buttonId="preview-button"
      buttonToolTipText={
        isProjectConnectedToShopify
          ? "Preview page in browser"
          : "Connect to Shopify to preview"
      }
      disabled={isPreviewDisabled}
      isButtonLoading={isButtonLoading}
      // NOTE (Kurt, 2024-12-26): We don't want to disable the InlinePopover for the
      // split button even when `isPreviewDisabled` is true because we show a "Connect to Shopify"
      // callout when the the project is not connected to Shopify.
      isButtonDisabled={!isProjectConnectedToShopify}
      onButtonClick={() => {
        void (async () => {
          setIsButtonLoading(true);
          logEvent("preview.requested", {});
          if (
            currentElementVersion === lastCreatedPreviewElementVersion &&
            currentElementId === lastCreatedPreviewElementId
          ) {
            window.open(previewUrl, "_blank");
          } else {
            const previewData = onCreatePreviewData();
            if (previewData) {
              await createPreviewElement(previewData);
              setLastCreatedPreviewElementVersion(
                currentElementVersion ?? null,
              );
              setLastCreatedPreviewElementId(currentElementId ?? null);
              showBundleBuilderPreviewWarningIfNecessary({
                previewData,
                shouldRedirect: true,
              });
            }
          }
          setIsButtonLoading(false);
        })();
      }}
      buttonText="Preview"
      popoverProps={{ ...popoverProps, dataTestId: "preview-popover" }}
      size="base"
      variant="secondary"
    />
  );
};

type PublishPreviewProps = {
  isPreviewCreateLoading: boolean;
  isPreviewCreateSuccessful: boolean;
};

const PublishPreview: React.FC<PublishPreviewProps> = (props) => {
  const { isPreviewCreateLoading, isPreviewCreateSuccessful } = props;
  const { url: previewUrl, previewUrlAvailable } =
    usePreviewBeforePublishInfo();

  const logEvent = useLogAnalytics();

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex w-full flex-col gap-1">
        <div className="text-default text-xs font-semibold">Browser</div>
        <div className="flex w-full flex-row gap-1">
          <DeprecatedButton
            variant="secondary"
            layoutClassName="grow"
            endEnhancer={
              !isPreviewCreateLoading ? <ArrowUpRight size={10} /> : undefined
            }
          >
            {isPreviewCreateLoading ? (
              <div className="flex items-center gap-2">
                <Spinner variant="default" />
              </div>
            ) : (
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={previewUrl}
                className="cursor-pointer"
              >
                View Preview in Browser
              </a>
            )}
          </DeprecatedButton>
          <DeprecatedIconButton
            variant="secondary"
            size="sm"
            icon={<Copy size={14} />}
            onClick={() => {
              copy(previewUrl);
              successToast(
                "Preview URL copied",
                "You can now open the Preview in a new window.",
              );
              logEvent("preview.copied", {});
            }}
          />
        </div>
      </div>
      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-col gap-1">
          <div className="text-default text-xs font-semibold">Mobile</div>
          <div className="text-muted text-xs font-normal">
            Scan the QR code with your mobile device to preview.
          </div>
        </div>
        <div className="flex items-center justify-center">
          {!isPreviewCreateLoading &&
          isPreviewCreateSuccessful &&
          previewUrlAvailable ? (
            <QRCode
              value={previewUrl}
              qrStyle="dots"
              bgColor="black"
              fgColor="white"
              logoImage="/replo-logo-quartercircle-black-bg.svg"
              logoWidth={60}
              removeQrCodeBehindLogo={true}
              quietZone={5}
              // NOTE (Kurt, 2024-12-04): A width of 190px aligns the QR code with
              // the width of the parent container
              size={190}
              eyeRadius={2}
            />
          ) : (
            <PreviewLinkAndSVGSkeleton />
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewPageButton;
